import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { REGION_NAMES } from "@/lib/regions";
import { Trash2, LayoutDashboard, UserPlus } from "lucide-react";

const EMPTY = { name: "", email: "", password: "", region: "" };

export default function OperatorsManager() {
  const [ops, setOps] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const load = async () => { try { const { data } = await api.get("/admin/operators"); setOps(data); } catch (e) { /* ignore */ } };
  useEffect(() => { load(); }, []);
  const taken = new Set(ops.map((o) => o.operator_region));
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/operators", form); toast.success(`${form.name} is now the ${form.region} operator`); setForm(EMPTY); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const del = async (o) => {
    if (!window.confirm(`Remove ${o.operator_name} as ${o.operator_region} operator? They lose access immediately.`)) return;
    try { await api.delete(`/admin/operators/${o.id}`); toast.success("Operator removed"); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  return (
    <Card data-testid="operators-card">
      <CardHeader>
        <CardTitle className="font-display text-2xl">Regional operators ({ops.length}/16)</CardTitle>
        <CardDescription>Each operator can only see and manage their own region. You can open any region&apos;s dashboard.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={create} className="grid sm:grid-cols-5 gap-2" data-testid="operator-form">
          <Input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="operator-name" />
          <Input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="operator-email" />
          <Input required minLength={8} type="text" placeholder="Temp password (8+)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="operator-password" />
          <Select value={form.region} onValueChange={(v) => setForm({ ...form, region: v })}>
            <SelectTrigger data-testid="operator-region"><SelectValue placeholder="Region" /></SelectTrigger>
            <SelectContent>{REGION_NAMES.filter((r) => !taken.has(r)).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
          <Button type="submit" disabled={!form.region} className="bg-emerald-800 hover:bg-emerald-900" data-testid="operator-create"><UserPlus className="w-4 h-4 mr-1" />Add</Button>
        </form>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {REGION_NAMES.map((r) => {
            const o = ops.find((x) => x.operator_region === r);
            return (
              <div key={r} className={`rounded-xl border p-3 text-sm ${o ? "border-emerald-200 bg-emerald-50/50" : "border-dashed border-slate-300"}`} data-testid={`region-op-${r}`}>
                <div className="font-bold">{r}</div>
                <div className="text-xs text-slate-500 truncate">{o ? `${o.operator_name} · ${o.email}` : "No operator yet"}</div>
                <div className="flex gap-1 mt-2">
                  <Link to={`/operator?region=${encodeURIComponent(r)}`}><Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`open-dash-${r}`}><LayoutDashboard className="w-3.5 h-3.5 mr-1" />Dashboard</Button></Link>
                  {o && <Button size="sm" variant="outline" className="h-7 text-xs text-red-700 border-red-200" onClick={() => del(o)} data-testid={`delete-op-${r}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
